// src/pages/admin/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Container from "../../components/Container";
import api from "../../api/axios"; // default 하나만 쓰면 됨
import { useTranslation } from "react-i18next";

export default function AdminLogin() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // ✅ 이미 로그인 상태라면 로그인 페이지 접근 시 관리자 페이지로 리다이렉트
  useEffect(() => {
    try {
      const storedUser = sessionStorage.getItem("user");
      if (!storedUser) return;

      const user = JSON.parse(storedUser);
      if (user && user.userType === "ADMIN") {
        // 이전에 보호 라우트에서 온 경우 되돌려 보내고, 아니면 /admin
        const from = location.state?.from?.pathname || "/admin";
        navigate(from, { replace: true });
      }
    } catch (e) {
      console.error("Failed to parse stored user:", e);
    }
  }, [navigate, location]);

  const validate = () => {
    if (!email.trim())
      return t("adminLogin.err.needEmail", "이메일을 입력해 주세요.");
    if (!password.trim())
      return t("adminLogin.err.needPassword", "비밀번호를 입력해 주세요.");
    return "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const msg = validate();
    if (msg) {
      setError(msg);
      return;
    }

    setSubmitting(true);
    try {
      const res = await api.post("/api/user/login/jwt", {
        email: email.trim(),
        password: password.trim(),
      });

      const resultCode = res.data?.resultCode;
      const token =
        res.data?.data?.accessToken || res.data?.data?.token || res.data?.token;

      if (resultCode === "200" || resultCode === 200) {
        if (token) {
          // 🔹 토큰 저장
          localStorage.setItem("adminAccessToken", token);
        }

        // 🔹 로그인 후 현재 유저 정보 가져오기
        const userResponse = await api.get("/api/user/detail");
        // console.log("userResponse", userResponse.data);

        const user = userResponse.data.data;
        sessionStorage.setItem("user", JSON.stringify(user));

        // 🔹 이동 (관리자 페이지로)
        navigate("/admin");
      } else {
        setError(
          res.data?.resultMessage ||
            "로그인에 실패했습니다. 다시 시도해 주세요."
        );
      }
    } catch (err) {
      console.error("Login failed:", err);
      setError(
        err.response?.data?.resultMessage ||
          t(
            "adminLogin.err.server",
            "로그인 중 문제가 발생했습니다. 다시 시도해 주세요."
          )
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Container className="flex min-h-screen items-center justify-center">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-md">
          <h1 className="text-2xl font-extrabold tracking-tight text-blue-900">
            {t("adminLogin.title", "관리자 로그인")}
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            {t(
              "adminLogin.desc",
              "관리자 전용 페이지입니다. 발급받은 계정으로 로그인해 주세요."
            )}
          </p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4">
            <div>
              <label
                htmlFor="admin-email"
                className="block text-sm font-medium text-slate-800"
              >
                {t("adminLogin.id", "이메일")}
                <span className="text-red-600">*</span>
              </label>
              <input
                id="admin-email"
                type="text"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t(
                  "adminLogin.ph.id",
                  "관리자 이메일을 입력해 주세요."
                )}
              />
            </div>

            <div>
              <label
                htmlFor="admin-password"
                className="block text-sm font-medium text-slate-800"
              >
                {t("adminLogin.password", "비밀번호")}
                <span className="text-red-600">*</span>
              </label>
              <input
                id="admin-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                placeholder={t(
                  "adminLogin.ph.password",
                  "비밀번호를 입력해 주세요."
                )}
              />
            </div>

            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 w-full rounded-xl bg-blue-600 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting
                ? t("adminLogin.submitting", "로그인 중...")
                : t("adminLogin.submit", "로그인")}
            </button>
          </form>
        </div>
      </Container>
    </div>
  );
}
